/* eslint-disable @typescript-eslint/no-unused-vars */
import { Injectable, EventEmitter } from '@angular/core';
import { Observable, Subject, Subscription, of } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { CompletionMessage, StreamChunkData } from '@app-libs/model';
import { IChatOrchestrator, ChatInitParams, ChatOperationState, ChatOperation } from './chat.types';
import { AuthService } from '../../auth/auth.service';
import { ChatStateService } from '../chat-state.service';
import { EventBusService, EventTypes } from '../../client/event-bus.service';
import { ChatApiService } from '../chat-api.service';
import { StreamProcessor } from '../streaming/stream-processor.service';
import { ChatType } from './chat.types';
import { ConfigurationClientService } from '../../client/configuration-client.service';
import { ConversationsApiService } from '../../api/conversations-api.service';
import { ChatInitializationService } from './chat-initialization.service';

/**
 * ChatOrchestrator - Main coordination service for chat operations
 *
 * This service orchestrates all chat operations including:
 * - Message sending coordination
 * - Stream management coordination
 * - State updates
 * - Error handling
 * - Event emission
 *
 * Migrated from ChatService as part of the refactoring to separate concerns
 */
@Injectable({
  providedIn: 'root',
})
export class ChatOrchestrator implements IChatOrchestrator {
  /**
   * Event emitter for scrolling the chat window to the bottom.
   */
  scrollToBottom: EventEmitter<void> = new EventEmitter<void>();

  private streamSubscription?: Subscription;

  constructor(
    private authService: AuthService,
    private chatStateService: ChatStateService,
    private eventBus: EventBusService,
    private chatApiService: ChatApiService,
    private streamProcessor: StreamProcessor,
    private config: ConfigurationClientService,
    private conversationsApiService: ConversationsApiService,
    private chatInitializationService: ChatInitializationService,
  ) {}

  /**
   * Sends a user's message and sets up a stream subscription for the assistant's response.
   * @param message The user's Message object (containing content, role, etc.).
   * @returns Observable that emits the complete assistant message when streaming is done
   */
  sendMessageStream(message: Partial<CompletionMessage>): Observable<CompletionMessage> {
    console.debug('ChatOrchestrator.sendMessageStream:', message);
    const completionSubject = new Subject<CompletionMessage>();

    try {
      // Update state with user message and processing flag
      this.chatStateService.update({
        userMessage: message.content,
        processing: true,
      });

      // Push the user's message to the local state
      this.chatStateService.addMessage(message);

      // Scroll to show the user's message
      console.debug('[ChatOrchestrator] Emitting scrollToBottom for user message');
      this.scrollToBottom.emit();

      // Save the model ID in the message
      message.model_id = this.chatStateService.selectedModel;

      // Create a conversation message in the database
      this.chatApiService.createUserMessage(message).subscribe({
        error: (error) => this.handleError('createUserMessage', error),
      });

      // Start streaming response
      const stream$ = this.getStreamingResponse(
        message,
        this.chatStateService.messages,
        this.chatStateService.selectedModel,
      );

      // Add a placeholder "assistant" message to the local state
      const botMessage = {
        content: '',
        role: 'assistant',
        user_id: this.authService.getCurrentUserId(),
        // Add a temporary ID for better trackBy performance during streaming
        message_id: `temp-${Date.now()}`,
        metadata: {
          tool_calls: [],
          streaming_tool_status: 'idle'
        }
      } as CompletionMessage;

      this.chatStateService.addMessage(botMessage);
      const botMessageIndex = this.chatStateService.messages.length - 1;

      // Reset current bot message
      this.chatStateService.currentBotMessage = '';

      this.subscribeToStream(stream$, completionSubject, botMessageIndex);
    } catch (error) {
      this.handleError('sendMessageStream', error);
      completionSubject.error(error);
    }

    return completionSubject.asObservable();
  }

  /**
   * Stops the current stream.
   */
  stopStream(): void {
    if (this.streamSubscription) {
      this.streamSubscription.unsubscribe();
      this.streamSubscription = undefined;
      this.updateProcessingFlag(false);
      console.debug('[ChatOrchestrator] Stream stopped - NOT emitting scrollToBottom');
      // Don't force scroll when stopping - let UI handle based on scroll state
    }
  }

  /**
   * Initializes a chat session based on the provided chat type and ID.
   * @param params Chat initialization parameters
   * @returns A promise that resolves when the chat is fully initialized.
   */
  async initChat(params: ChatInitParams): Promise<void> {
    console.debug(`ChatOrchestrator.initChat ${params.chatType}/${params.id}`);
    await this.chatInitializationService.initChat(params.chatType, params.id);
  }

  /**
   * Resets the entire chat back to its default state.
   */
  resetChat(): void {
    this.chatStateService.resetState();
  }

  /**
   * Get current chat state
   */
  getCurrentState(): ChatOperationState {
    return {
      processing: this.chatStateService.processing,
      messages: this.chatStateService.messages,
      selectedModel: this.chatStateService.selectedModel,
      workspaceId: this.chatStateService.workspaceId,
      chatType: this.chatStateService.chatType,
      conversation: this.chatStateService.conversation as any,
      assistant: this.chatStateService.assistant as any,
    };
  }

  /**
   * Gets the appropriate streaming response using unified backend logic
   * 
   * PHASE 4 SIMPLIFICATION: Replaced complex 3-path conditional logic with single call
   * 
   * Previous logic (complex):
   * - Assistant detection → Frontend conditional logic
   * - Feature flag checking → Frontend environment checks  
   * - Parameter transformation → Multiple frontend transformation paths
   * 
   * New logic (simple):
   * - Single call to unified method → Backend handles ALL conditional logic
   * - ~70% reduction in frontend complexity
   * - Backend ChatTypeDetectionService, ParameterTransformationService, FeatureFlagService
   */
  private getStreamingResponse(
    userMessage: Partial<CompletionMessage>,
    messages: Partial<CompletionMessage>[],
    useModel?: string,
  ): Observable<StreamChunkData> {
    // Single unified call - backend handles all complexity
    return this.chatApiService.createUnifiedCompletionStream(
      messages,
      useModel || this.config.defaultModel,
      {
        signal: undefined,
        currentUserMessage: userMessage,
        // TODO: Add temperature and maxTokens when implemented in ChatStateService
      }
    );
  }

  /**
   * Subscribes to stream and handles all stream events
   */
  private subscribeToStream(
    stream$: Observable<StreamChunkData>,
    completionSubject: Subject<CompletionMessage>,
    botMessageIndex: number,
  ): void {
    let final_data: StreamChunkData | undefined;
    const allChunks: StreamChunkData[] = [];
    console.debug('ChatOrchestrator: Subscribed to stream');

    // Initialize stream processor with message index
    this.streamProcessor.initializeStream(botMessageIndex);

    this.streamSubscription = stream$
      .pipe(
        finalize(() => {
          // Force final update to ensure last chunks are displayed
          if (botMessageIndex >= 0) {
            this.chatStateService.updateMessageByIndex(botMessageIndex, {
              content: this.chatStateService.currentBotMessage,
            });
          }
        }),
      )
      .subscribe({
        next: async (chunk: StreamChunkData) => {
          // Use StreamProcessor instead of inline processing
          this.streamProcessor.processChunk(chunk);
          
          // Store all chunks for processing
          allChunks.push(chunk);

          // Save final chunk for usage tracking
          if (chunk.type === 'final') {
            final_data = chunk;
          }
        },
        error: async (error) => {
          this.streamProcessor.handleStreamError(error);
          this.handleStreamError(error, botMessageIndex);
        },
        complete: async () => {
          try {
            // Find final chunk from all chunks as fallback
            if (!final_data) {
              final_data = allChunks.find(chunk => chunk.type === 'final');
            }
            
            this.streamProcessor.handleStreamComplete();
            const savedMessage = await this.handleStreamComplete(final_data, botMessageIndex);
            completionSubject.next(savedMessage);
            completionSubject.complete();
          } catch (error) {
            completionSubject.error(error);
          }
        },
      });
  }


  /**
   * Handles stream errors
   */
  private handleStreamError(error: any, botMessageIndex: number): void {
    console.error('ChatOrchestrator: Stream error:', error);
    // Update message with error content
    this.chatStateService.updateMessageByIndex(botMessageIndex, {
      content: `*${error}*`,
    });
    this.fireCompleteEvents(true);
  }

  /**
   * Handles stream completion
   */
  private async handleStreamComplete(
    final_chunk?: StreamChunkData,
    botMessageIndex?: number,
  ): Promise<CompletionMessage> {
    console.debug('ChatOrchestrator: Stream complete:', final_chunk);
    let complete_fired = false;
    const usage = final_chunk?.data?.usage || final_chunk?.data?.data?.usage;

    try {
      complete_fired = this.fireCompleteEvents(false, usage);

      // Extract thread_id from final chunk if present (from assistant API)
      const threadId = final_chunk?.data?.thread_id || final_chunk?.data?.data?.thread_id;
      if (threadId && !this.chatStateService.threadId) {
        console.debug('Setting thread ID from stream response:', threadId);
        this.chatStateService.threadId = threadId;
        
        // Update conversation with thread_id if we have one
        const conversationId = this.chatStateService.conversation?.conversation_id;
        if (conversationId) {
          await this.conversationsApiService.updateConversationThread(conversationId, threadId).toPromise();
        }
      }

      // Get tool calls from stream processor
      const toolCalls = this.streamProcessor.getCurrentToolCalls();
      
      // Save the assistant's full message with tool calls
      const savedMessage = await this.chatApiService.createAssistantMessage(usage, toolCalls).toPromise();

      if (!savedMessage) {
        throw new Error('Failed to save assistant message');
      }

      // Update the message with the real ID and merge metadata to preserve streaming status
      if (botMessageIndex !== undefined) {
        const currentMessage = this.chatStateService.messages[botMessageIndex];
        const existingStreamingStatus = currentMessage?.metadata?.['streaming_tool_status'];
        
        // CRITICAL: We must merge metadata, not replace it
        // The savedMessage.metadata contains tool_calls from the database
        // But we need to preserve streaming_tool_status that was set during streaming
        this.chatStateService.updateMessageByIndex(botMessageIndex, {
          message_id: savedMessage.message_id,
          metadata: {
            ...savedMessage.metadata,
            // Preserve streaming status if it exists (e.g., 'completed', 'failed')
            ...(existingStreamingStatus ? { streaming_tool_status: existingStreamingStatus } : {})
          }
        });
      }

      // Reset the current bot message state
      this.chatStateService.currentBotMessage = '';

      return savedMessage;
    } catch (error) {
      this.handleError('handleStreamComplete', error);
      complete_fired = this.fireCompleteEvents(true);
      throw error;
    } finally {
      if (!complete_fired) {
        this.fireCompleteEvents();
      }
    }
  }

  /**
   * Fires completion events
   */
  private fireCompleteEvents(isError = false, data?: any): boolean {
    this.eventBus.sendStreamComplete({ isError: isError, message: data });
    this.updateProcessingFlag(false);
    return true;
  }

  /**
   * Updates the processing flag in state
   */
  private updateProcessingFlag(processing: boolean): void {
    this.chatStateService.update({ processing });
  }

  /**
   * Handles errors with consistent logging
   */
  private handleError(operation: string, error: any): void {
    console.error(`ChatOrchestrator.${operation}:`, error);
    this.updateProcessingFlag(false);
    // Don't force scroll on error - let UI handle based on scroll state
  }

  /**
   * Determines if the current chat is a conversation load (optional usage).
   */
  isConversationLoad(): boolean {
    return this.chatStateService.chatType === ChatType.Conversation;
  }

  /**
   * Checks if uploaded file can be removed
   */
  removeUploadedFile(): boolean {
    return !this.chatApiService.isAssistantChatExternal();
  }

  /**
   * Updates the selected model
   */
  onUpdateSelectedModel(model: string): void {
    console.debug('ChatOrchestrator.onUpdateSelectedModel:', model);
    this.chatStateService.selectedModel = model;
  }

  /**
   * Transcribes audio to text
   */
  transcribeAudio(audioBlob: Blob): Promise<string> {
    return this.chatApiService
      .transcribeAudio(audioBlob)
      .toPromise()
      .then((result) => result || '');
  }

  /**
   * Methods for components to use instead of direct state access
   */
  setProcessing(processing: boolean): void {
    this.chatStateService.processing = processing;
  }

  getUploadedFile(): any {
    return this.chatStateService.uploadedFile;
  }

  setUploadedFile(file: any): void {
    this.chatStateService.uploadedFile = file;
  }
}
